import Service, { hours } from './Service';
import type TokenizedApi from '../TokenizedApi';
import type { TDisplayContact, TLockbox } from '../TokenizedApi';
import * as schema from '../utils/schema';
import { urlPath } from '../utils/utils';
import {
  restApiPendingTransaction,
  restApiNewActivityCreated,
  TActivityDetails,
} from './TokenizedApiModelEntities';
import { Instrument } from '../utils/instruments';
import { MFARequest } from './MFA';

/** @category Instruments */
export interface TInstrumentSendRecipient {
  /**
   * The paymail handle of the recipient.
   */
  handle?: string;
  /**
   * (Instead of `handle`) the BSV address of the recipient.
   * Only accepted when sending BSV amounts.
   */
  address?: string;
  /**
   * The instrument and amount to send to this recipient, specified as
   * an {@linkcode Instrument} instance.
   */
  amount: Instrument;
  /**
   * (Instead of `amount`) When `true`, send the maximum available amount
   * of the instrument (minus fees) to this recipient. Can only be set for
   * one recipient.
   */
  sendMax?: boolean;
}

/**
 * Instrument transfers - send and trade instruments.
 *
 * @category SDK Modules
 */
class Transfers extends Service {
  /** @internal */
  constructor(tokenizedApi: TokenizedApi) {
    super(tokenizedApi);
  }

  makeActivityEventForUnconfirmedSend({
    profileId,
    lockboxId,
    recipient,
    instrument,
    sendMax,
    memo,
    isConnecting,
    isConfirming,
    lockboxes,
    ownHandle,
  }: {
    profileId: string;
    lockboxId: string;
    recipient: TDisplayContact;
    instrument: Instrument;
    sendMax?: boolean;
    memo?: string;
    isConnecting?: boolean;
    isConfirming?: boolean;
    lockboxes: {
      lockboxes: TLockbox[];
      idMap: Map<string, TLockbox>;
    };
    ownHandle: string;
  }): TActivityDetails {
    let stage = 'creating';
    if (isConfirming) {
      stage = 'confirming';
    } else if (isConnecting) {
      stage = 'connecting';
    }

    return this.tokenizedApi.activity.transformEvent(
      {
        id: '',
        lockboxId,
        activityEventType: 'payment',
        stage,
        isInitiator: true,
        instruments: [
          {
            instrumentId: instrument?.instrumentId,
          },
        ],
        counterparties: [
          {
            displayHandle: recipient?.address || recipient?.displayHandle,
            displayName: recipient?.displayName,
            isAnonymous: recipient?.isAnonymous,
            isInternal: recipient?.isInternal,
            isVerified: recipient?.isVerified,
            transfers: [
              {
                instrumentId: instrument?.instrumentId,
                direction: 'sent',
                quantity: instrument?.quantity,
              },
            ],
          },
        ],
        transactions: [
          {
            // This is the minimum necessary to
            // get the transform to identify this as a
            // proposal. Won’t be looked at for non-multisig.
            isProposer: true,
            // So that the activity details timeline can show the signers
            signers: lockboxId && lockboxes?.idMap?.get(lockboxId)?.signers,
          },
        ],
        memo,
        fees: {},
      },
      { profileId, lockboxes, ownHandle },
    );
  }

  /**
   * Send an amount of a specified instrument to one or more other entities.
   *
   * @returns The activity event ID of the confirmed transfer
   */
  async send({
    profileId,
    lockboxId,
    recipients,
    memo,
    setMfaRequest,
  }: {
    profileId: string;
    /** The lockbox containing the instruments to be sent */
    lockboxId: string;
    /**
     * The entities to send to (paymail handles or addresses),
     * and the amount to send to each
     */
    recipients: TInstrumentSendRecipient[];
    /** An optional message for the recipients */
    memo?: string;
    setMfaRequest: (mfaRequest: MFARequest | null) => void;
  }) {
    if (!profileId?.length || typeof profileId !== 'string') {
      throw new TypeError('Send: profileId must be a non-empty string');
    }
    if (!lockboxId?.length || typeof lockboxId !== 'string') {
      throw new TypeError('Send: lockboxId must be a non-empty string');
    }

    const description = memo;

    let instrumentId: string | null = null;

    const checkedRecipients: (
      | {
          amount: number;
          send_max: boolean;
          handle: string;
        }
      | {
          amount: number;
          send_max: boolean;
          address: string;
        }
    )[] = await Promise.all(
      recipients.map(async ({ address, handle, amount, sendMax }) => {
        if (!Instrument.isInstrument(amount)) {
          throw new Error('Transfer amount is not an Instrument instance');
        }
        if (instrumentId) {
          if (instrumentId !== amount.instrumentId) {
            throw new Error('Cannot send multiple instruments in one transfer');
          }
        } else {
          if (!amount.instrumentId) {
            throw new Error('Invalid instrument in transfer');
          }
          instrumentId = amount.instrumentId;
        }
        if (!amount.hasQuantity) {
          throw new Error(`Invalid amount in transfer`);
        }

        const recipient = handle || address;
        if (!recipient || typeof recipient !== 'string') {
          throw new TypeError(`Invalid recipient: ${recipient}`);
        }
        const contactInfo =
          await this.tokenizedApi.relationships.handleQuery.fetch({
            handle: recipient,
          });
        if (!contactInfo?.isValid) {
          throw new TypeError(`Invalid recipient: ${recipient}`);
        }
        let recipientProperty;
        if (contactInfo.isInternal) {
          recipientProperty = { handle: recipient };
        } else {
          if (contactInfo.isAnonymous) {
            recipientProperty = { address: recipient };
          } else {
            // external paymail
            recipientProperty = { handle: recipient };
          }
        }

        return {
          send_max: !!sendMax,
          ...recipientProperty,
          amount: sendMax ? 0 : amount.quantity,
        };
      }),
    );

    if (!instrumentId) {
      throw new Error('No instrument specified in transfer');
    }

    const path = urlPath('profiles', profileId, 'send');

    const { clientId } = await this.tokenizedApi.getApplicationIdentifiers();
    const handshakeResponse = await this.tokenizedApi.endpoints.put(path, {
      lock_box_id: lockboxId,
      instrument: instrumentId,
      description,
      recipients: checkedRecipients,
      handshake_client_id: clientId,
      handshake_signing: true,
    });
    const { handshakeId, activityId } =
      schema.normalizeResponse(
        'Send instruments handshake',
        handshakeResponse.data.data,
        restApiNewActivityCreated,
      ).normalizedValue || {};

    if (!activityId) {
      throw new Error('The send operation could not be started');
    }

    // Handshake (mobile app confirmation) is optional
    if (handshakeId) {
      await this.tokenizedApi.mfa.waitForMfa(
        handshakeId,
        setMfaRequest,
        activityId,
      );
    }

    return activityId;
  }

  makeActivityEventForUnconfirmedRequest({
    profileId,
    lockboxId,
    recipient,
    instrument,
    memo,
    isConnecting,
    isConfirming,
    lockboxes,
    ownHandle,
  }: {
    profileId: string;
    lockboxId: string;
    recipient: TDisplayContact;
    instrument: Instrument;
    memo?: string;
    isConnecting?: boolean;
    isConfirming?: boolean;
    lockboxes: {
      lockboxes: TLockbox[];
      idMap: Map<string, TLockbox>;
    };
    ownHandle: string;
  }): TActivityDetails {
    let stage = 'creating';
    if (isConfirming) {
      stage = 'confirming';
    } else if (isConnecting) {
      stage = 'connecting';
    }

    return this.tokenizedApi.activity.transformEvent(
      {
        id: '',
        lockboxId,
        activityEventType: 'trade_offer',
        stage,
        isInitiator: true,
        instruments: [
          {
            instrumentId: instrument?.instrumentId,
          },
        ],
        counterparties: [
          {
            displayHandle: recipient?.address || recipient?.displayHandle,
            displayName: recipient?.displayName,
            isAnonymous: recipient?.isAnonymous,
            isInternal: recipient?.isInternal,
            isVerified: recipient?.isVerified,
            transfers: [
              {
                instrumentId: instrument?.instrumentId,
                direction: 'received',
                quantity: instrument?.quantity,
              },
            ],
          },
        ],
        transactions: [
          {
            // This is the minimum necessary to
            // get the transform to identify this as a
            // proposal. Won’t be looked at for non-multisig.
            isProposer: true,
            // Instrument requests sent by the SDK always
            // use internal requests
            isInternal: true,
          },
        ],
        memo,
        fees: {},
      },
      { profileId, lockboxes, ownHandle },
    );
  }

  /**
   * Request an instrument to be sent to you by another entity. Requests
   * are received in the form of an open activity item, which can be accepted
   * or declined by the recipient using the {@linkcode Transfers.approve} and
   * {@linkcode Transfers.reject} methods.
   *
   * @returns The activity event ID of the confirmed request
   */
  async request({
    profileId,
    lockboxId,
    recipient,
    amount,
    memo,
    expiry = new Date(Date.now() + hours(48)).toISOString(),
    setMfaRequest,
  }: {
    profileId: string;
    /** Lockbox into which the amount will be transferred */
    lockboxId: string;
    /** Paymail handle (only Tokenized handles supported) of the recipient */
    recipient: string;
    /**
     * The instrument and amount being requested, specified as
     * an {@linkcode Instrument} instance.
     */
    amount: Instrument;
    /** An optional message to show the recipient with the request */
    memo?: string;
    /**
     * An ISO-8601-format timestamp in the future when the request
     * should expire (default is two days from now)
     */
    expiry?: string;
    setMfaRequest: (mfaRequest: MFARequest | null) => void;
  }) {
    if (!profileId?.length || typeof profileId !== 'string') {
      throw new TypeError('Request: profileId must be a non-empty string');
    }
    if (!lockboxId?.length || typeof lockboxId !== 'string') {
      throw new TypeError('Request: lockboxId must be a non-empty string');
    }
    if (!Instrument.isInstrument(amount)) {
      throw new Error('Request: amount is not an Instrument instance');
    }
    if (!amount.instrumentId) {
      throw new Error('Request: Invalid instrument');
    }
    if (!amount.hasQuantity) {
      throw new Error('Request: Invalid amount');
    }

    if (!recipient || typeof recipient !== 'string') {
      throw new TypeError(`Invalid recipient: ${recipient}`);
    }
    const contactInfo = await this.tokenizedApi.relationships.handleQuery.fetch(
      { handle: recipient },
    );
    if (!contactInfo?.isValid) {
      throw new TypeError(`Invalid recipient : ${recipient}`);
    }

    const path = urlPath('profiles', profileId, 'requests', 'send');

    const { clientId } = await this.tokenizedApi.getApplicationIdentifiers();
    const handshakeResponse = await this.tokenizedApi.endpoints.put(path, {
      lock_box_id: lockboxId,
      recipient,
      instrument: amount.instrumentId,
      amount: amount.quantity,
      description: memo,
      expiry,
      handshake_client_id: clientId,
      handshake_signing: true,
    });
    const { activityId, handshakeId } =
      schema.normalizeResponse(
        'Request instruments handshake',
        handshakeResponse.data.data,
        restApiNewActivityCreated,
      ).normalizedValue || {};

    if (!activityId) {
      throw new Error('The request operation could not be started');
    }

    // Handshake (mobile app confirmation) is optional
    if (handshakeId) {
      await this.tokenizedApi.mfa.waitForMfa(
        handshakeId,
        setMfaRequest,
        activityId,
      );
    }

    return activityId;
  }

  makeActivityEventForUnconfirmedTrade({
    profileId,
    lockboxId,
    recipient,
    sendInstrument,
    receiveInstrument,
    memo,
    isConfirming,
    lockboxes,
    ownHandle,
  }: {
    profileId: string;
    lockboxId: string;
    recipient: TDisplayContact;
    sendInstrument: Instrument;
    receiveInstrument: Instrument;
    memo?: string;
    isConfirming?: boolean;
    lockboxes: {
      lockboxes: TLockbox[];
      idMap: Map<string, TLockbox>;
    };
    ownHandle: string;
  }): TActivityDetails {
    return this.tokenizedApi.activity.transformEvent(
      {
        id: '',
        lockboxId,
        activityEventType: 'trade_offer',
        stage: isConfirming ? 'confirming' : 'creating',
        isInitiator: true,
        counterparties: [
          {
            displayHandle: recipient?.address || recipient?.displayHandle,
            displayName: recipient?.displayName,
            isAnonymous: recipient?.isAnonymous,
            isInternal: recipient?.isInternal,
            isVerified: recipient?.isVerified,
            transfers: [
              {
                instrumentId: sendInstrument?.instrumentId,
                direction: 'sent',
                quantity: sendInstrument?.quantity,
              },
              {
                instrumentId: receiveInstrument?.instrumentId,
                direction: 'received',
                quantity: receiveInstrument?.quantity,
              },
            ],
          },
        ],
        transactions: [
          {
            // This is the minimum necessary to
            // get the transform to identify this as a
            // proposal. Won’t be looked at for non-multisig.
            isProposer: true,
            // Trades initiated by the SDK always
            // use internal requests
            isInternal: true,
            // Trades initiated by the SDK always use
            // the 2-stage (ACP) process
            is2StageExchange: true,
            // So that the activity details timeline can show the signers
            signers: lockboxId && lockboxes?.idMap?.get(lockboxId)?.signers,
          },
        ],
        memo,
        fees: {},
      },
      { profileId, lockboxes, ownHandle },
    );
  }

  /**
   * Initiate a trade of instruments with another entity by sending them an offer. Offers are
   * received in the form of an open activity item, which can be accepted or declined
   * using the {@linkcode Transfers.approve} and {@linkcode Transfers.reject} methods.
   * The final step is for the initiator to countersign (or decline) the resulting accepted
   * activity event (using the same methods).
   *
   * @returns The activity event ID of the confirmed trade
   */
  async trade({
    profileId,
    lockboxId,
    recipient,
    sendAmount,
    receiveAmount,
    memo,
    expiry = new Date(Date.now() + hours(48)).toISOString(),
    setMfaRequest,
  }: {
    profileId: string;
    /**
     * Lockbox from which the offered amount will be taken, and into which the
     * requested amount will be transferred
     */
    lockboxId: string;
    /** Paymail handle (only Tokenized handles supported) of the recipient of the offer */
    recipient: string;
    /**
     * The instrument and amount to offer to the recipient, specified as
     * an {@linkcode Instrument} instance.
     */
    sendAmount: Instrument;
    /**
     * The instrument and amount being requested from the recipient, specified as
     * an {@linkcode Instrument} instance.
     */
    receiveAmount: Instrument;
    /** An optional message to show the recipient with the offer */
    memo?: string;
    /** An ISO-8601-format timestamp in the future when the
     * offer should expire (default is two days from now)
     */
    expiry?: string;
    setMfaRequest: (mfaRequest: MFARequest | null) => void;
  }) {
    if (!profileId?.length || typeof profileId !== 'string') {
      throw new TypeError('Trade: profileId must be a non-empty string');
    }
    if (!lockboxId?.length || typeof lockboxId !== 'string') {
      throw new TypeError('Trade: lockboxId must be a non-empty string');
    }
    if (!Instrument.isInstrument(sendAmount)) {
      throw new Error('Trade: send amount is not an Instrument instance');
    }
    if (!sendAmount.instrumentId) {
      throw new Error('Trade: Invalid send instrument');
    }
    if (!sendAmount.hasQuantity) {
      throw new Error('Trade: Invalid send amount');
    }
    if (!Instrument.isInstrument(receiveAmount)) {
      throw new Error('Trade: receive amount is not an Instrument instance');
    }
    if (!receiveAmount.instrumentId) {
      throw new Error('Trade: Invalid receive instrument');
    }
    if (!receiveAmount.hasQuantity) {
      throw new Error('Trade: Invalid receive amount');
    }

    if (!recipient || typeof recipient !== 'string') {
      throw new TypeError(`Invalid recipient: ${recipient}`);
    }
    const contactInfo = await this.tokenizedApi.relationships.handleQuery.fetch(
      { handle: recipient },
    );
    if (!contactInfo?.isValid) {
      throw new TypeError(`Invalid recipient: ${recipient}`);
    }

    const path = urlPath('profiles', profileId, 'requests', 'swap');

    const { clientId } = await this.tokenizedApi.getApplicationIdentifiers();
    const response = await this.tokenizedApi.endpoints.put(path, {
      lock_box_id: lockboxId,
      recipient,
      send_instrument: sendAmount.instrumentId,
      send_amount: sendAmount.quantity,
      receive_instrument: receiveAmount.instrumentId,
      receive_amount: receiveAmount.quantity,
      expiry,
      description: memo,
      handshake_client_id: clientId,
      handshake_signing: true,
    });
    const { activityId, handshakeId } =
      schema.normalizeResponse(
        'Trade instruments handshake',
        response.data.data,
        restApiNewActivityCreated,
      ).normalizedValue || {};

    if (!activityId) {
      throw new Error('The trade operation could not be started');
    }

    // Handshake (mobile app confirmation) is optional
    if (handshakeId) {
      await this.tokenizedApi.mfa.waitForMfa(
        handshakeId,
        setMfaRequest,
        activityId,
      );
    }

    return activityId;
  }

  /**
   * Accept a received trade offer or transfer request (as the recipient), or execute an
   * accepted trade (as the initiator).
   *
   * @returns The activity event ID of the confirmed transfer
   */
  async approve({
    profileId,
    lockboxId,
    activityId,
    setMfaRequest,
  }: {
    profileId: string;
    /**
     * Lockbox from which sent amounts will be
     * taken, and into which received amounts will be transferred
     */
    lockboxId: string;
    /** The ID of the open activity event being accepted/executed. */
    activityId: string;
    setMfaRequest?: (mfaRequest: MFARequest | null) => void;
  }) {
    if (!profileId?.length || typeof profileId !== 'string') {
      throw new TypeError('Approve: profileId must be a non-empty string');
    }
    if (!lockboxId?.length || typeof lockboxId !== 'string') {
      throw new TypeError('Approve: lockboxId must be a non-empty string');
    }
    if (!activityId?.length || typeof activityId !== 'string') {
      throw new TypeError('Approve: activityId must be a non-empty string');
    }

    const path = urlPath('profiles', profileId, 'requests', 'approve');

    const { clientId } = await this.tokenizedApi.getApplicationIdentifiers();
    const handshakeResponse = await this.tokenizedApi.endpoints.put(path, {
      lock_box_id: lockboxId,
      activity_id: activityId,
      handshake_client_id: clientId,
      handshake_signing: true,
    });
    const handshakeId = schema.normalizeResponse(
      'Approve trade handshake',
      handshakeResponse.data.data,
      restApiNewActivityCreated,
    ).normalizedValue?.handshakeId;

    // Handshake (mobile app confirmation) is optional
    if (handshakeId) {
      await this.tokenizedApi.mfa.waitForMfa(
        handshakeId,
        setMfaRequest,
        activityId,
      );
    }

    return activityId;
  }

  /**
   * Decline a received trade offer or transfer request (as the recipient), or decline an
   * accepted trade (as the initiator).
   *
   * @returns The activity event ID of the declined transfer
   */
  async reject({
    profileId,
    lockboxId,
    activityId,
    memo,
    setMfaRequest,
  }: {
    profileId: string;
    /**
     * Lockbox from which sent amounts will be
     * taken, and into which received amounts will be transferred
     */
    lockboxId: string;
    /** The ID of the open activity event being declined. */
    activityId: string;
    /** An optional message explaining the reason for declining */
    memo?: string;
    setMfaRequest?: (mfaRequest: MFARequest | null) => void;
  }) {
    if (!profileId?.length || typeof profileId !== 'string') {
      throw new TypeError('Reject: profileId must be a non-empty string');
    }
    if (!lockboxId?.length || typeof lockboxId !== 'string') {
      throw new TypeError('Reject: lockboxId must be a non-empty string');
    }
    if (!activityId?.length || typeof activityId !== 'string') {
      throw new TypeError('Reject: activityId must be a non-empty string');
    }

    const { clientId } = await this.tokenizedApi.getApplicationIdentifiers();

    const path = urlPath('profiles', profileId, 'requests', 'reject');

    const handshakeResponse = await this.tokenizedApi.endpoints.put(path, {
      lock_box_id: lockboxId,
      activity_id: activityId,
      description: memo,
      handshake_client_id: clientId,
      handshake_signing: true,
    });
    const handshakeId = schema.normalizeResponse(
      'Reject trade handshake',
      handshakeResponse.data.data,
      restApiNewActivityCreated,
    ).normalizedValue?.handshakeId;

    // Handshake (mobile app confirmation) is optional
    if (handshakeId) {
      await this.tokenizedApi.mfa.waitForMfa(
        handshakeId,
        setMfaRequest,
        activityId,
      );
    }

    return activityId;
  }

  /** @internal */
  async acceptLockboxProposal(profileId: string, lockboxId: string) {
    // It’s convenient to put this in here, alongside other stuff
    // that triggers mobile-app signing

    const url = urlPath(
      'profiles',
      profileId,
      'lock_boxes',
      lockboxId,
      'proposal',
    );

    const { clientId } = await this.tokenizedApi.getApplicationIdentifiers();

    const response = await this.tokenizedApi.endpoints.post(url, {
      handshake_client_id: clientId,
    });
    const { handshakeId } =
      schema.normalizeResponse(
        'Retrieve lockbox proposal details',
        response?.data?.data,
        restApiNewActivityCreated,
      ).normalizedValue || {};

    return { handshakeId };
  }

  /** @internal */
  async rejectLockboxProposal(profileId: string, lockboxId: string) {
    // This isn’t signing, but let’s keep it next to acceptLockboxProposal
    await this.tokenizedApi.endpoints.delete(
      urlPath('profiles', profileId, 'lock_boxes', lockboxId, 'proposal'),
    );
  }

  /** @internal */
  async signProposal({
    profileId,
    pendingTransactionId,
    setMfaRequest,
  }: {
    profileId: string;
    pendingTransactionId: string;
    setMfaRequest?: (mfaRequest: MFARequest | null) => void;
  }) {
    if (!profileId?.length || typeof profileId !== 'string') {
      throw new TypeError(
        'Sign proposal: profileId must be a non-empty string',
      );
    }
    if (
      !pendingTransactionId?.length ||
      typeof pendingTransactionId !== 'string'
    ) {
      throw new TypeError(
        'Sign proposal: pendingTransactionId must be a non-empty string',
      );
    }

    const path = urlPath(
      'profiles',
      profileId,
      'pending_transactions',
      pendingTransactionId,
    );

    const transactionResponse = await this.tokenizedApi.endpoints.get(path);
    const { normalizedValue: pendingTransaction } = schema.normalizeResponse(
      'Fetch pending transaction',
      transactionResponse.data.data,
      restApiPendingTransaction,
    );
    if (!pendingTransaction?.inputSupplements?.length) {
      return null;
    }

    const { clientId } = await this.tokenizedApi.getApplicationIdentifiers();
    const handshakeResponse = await this.tokenizedApi.endpoints.put(path, {
      handshake_client_id: clientId,
      handshake_signing: true,
    });
    const handshakeId = schema.normalizeResponse(
      'Sign proposal handshake',
      handshakeResponse.data.data,
      restApiNewActivityCreated,
    ).normalizedValue?.handshakeId;

    // Handshake (mobile app confirmation) is optional
    if (handshakeId) {
      await this.tokenizedApi.mfa.waitForMfa(handshakeId, setMfaRequest);
    }
  }

  /** @internal */
  async rejectProposal({
    profileId,
    requestId,
    setMfaRequest,
  }: {
    profileId: string;
    requestId: string;
    setMfaRequest: (mfaRequest: MFARequest | null) => void;
  }) {
    if (!profileId?.length || typeof profileId !== 'string') {
      throw new TypeError(
        'Reject proposal: profileId must be a non-empty string',
      );
    }
    if (!requestId?.length || typeof requestId !== 'string') {
      throw new TypeError(
        'Reject proposal: requestId must be a non-empty string',
      );
    }

    const path = urlPath(
      'profiles',
      profileId,
      'pending_transactions',
      'request_id',
      requestId,
      'reject',
    );

    const { clientId } = await this.tokenizedApi.getApplicationIdentifiers();
    const handshakeResponse = await this.tokenizedApi.endpoints.put(path, {
      handshake_client_id: clientId,
    });
    const handshakeId = schema.normalizeResponse(
      'Reject proposal handshake',
      handshakeResponse.data.data,
      restApiNewActivityCreated,
    ).normalizedValue?.handshakeId;

    // Handshake (mobile app confirmation) is optional
    if (handshakeId) {
      await this.tokenizedApi.mfa.waitForMfa(handshakeId, setMfaRequest);

      await this.tokenizedApi.endpoints.post(path, {
        handshake_id: handshakeId,
      });
    }
  }

  /** @internal */
  async cancelProposal({
    profileId,
    requestId,
  }: {
    profileId: string;
    requestId: string;
  }) {
    if (!profileId?.length || typeof profileId !== 'string') {
      throw new TypeError(
        'Cancel proposal: profileId must be a non-empty string',
      );
    }
    if (!requestId?.length || typeof requestId !== 'string') {
      throw new TypeError(
        'Cancel proposal: requestId must be a non-empty string',
      );
    }

    const path = urlPath(
      'profiles',
      profileId,
      'pending_transactions',
      'request_id',
      requestId,
      'cancel',
    );

    await this.tokenizedApi.endpoints.post(path, {});
  }

  /** @internal */
  async cancelActivity({
    profileId,
    activityId,
  }: {
    profileId: string;
    activityId: string;
  }) {
    if (!profileId?.length || typeof profileId !== 'string') {
      throw new TypeError(
        'Cancel proposal: profileId must be a non-empty string',
      );
    }
    if (!activityId?.length || typeof activityId !== 'string') {
      throw new TypeError(
        'Cancel activity: activityId must be a non-empty string',
      );
    }

    const path = urlPath(
      'profiles',
      profileId,
      'activity',
      activityId,
      'cancel',
    );

    await this.tokenizedApi.endpoints.post(path, {});
  }
}

export default Transfers;
